import React, { useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { Receipt, ArrowLeft, Camera, Loader2, ScanLine, CheckCircle2, Trash2, Save, Search, TrendingDown, Clock, Image as ImageIcon, RotateCcw, Package, ShoppingCart, ListPlus, Heart, MapPin, Navigation, Pencil, X, StickyNote, Tag, ChevronRight, ShieldCheck } from 'lucide-react';
import { API, axiosInstance, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MyProductsPanel, ShoppingListPanel } from './PriceShopping';
import { FavouritesPanel, DealsPanel } from './PriceEngage';
import { PriceTrend } from '../components/PriceTrend';

// Reuses Find-2-Day's existing photo pipeline (browser-image-compression +
// heic2any, the same libraries the community uploader uses) so we do not build
// a brand new uploader. The receipt photo is processed in memory and NEVER
// stored — only the structured data the user confirms is saved.
const COMPRESS_OPTS = {
  maxSizeMB: 5,
  maxWidthOrHeight: 2000,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.82,
};

const heicToJpeg = async (file) => {
  const heic2any = (await import('heic2any')).default;
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const out = Array.isArray(blob) ? blob[0] : blob;
  return new File([out], (file.name || 'receipt').replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' });
};

const isHeic = (file) => {
  const n = (file.name || '').toLowerCase();
  const t = (file.type || '').toLowerCase();
  return n.endsWith('.heic') || n.endsWith('.heif') || t === 'image/heic' || t === 'image/heif';
};

// Parse a user-typed value into a number (or null). Accepts comma decimals.
const num = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

// Small labelled field for the editable review form.
const Field = ({ label, value, onChange, testId, placeholder }) => (
  <label className="block">
    <span className="text-[11px] text-gray-400 block mb-0.5">{label}</span>
    <Input
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || ''}
      className="h-9 text-sm"
      data-testid={testId}
    />
  </label>
);

// Date field with a native calendar picker. Opens around today, allows older
// dates, and keeps the stored value in the exact YYYY-MM-DD format the backend
// expects. Typing into the field still works as a manual fallback.
const DateField = ({ label, value, onChange, testId }) => {
  const today = new Date().toISOString().slice(0, 10);
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value || '') ? value : '';
  return (
    <label className="block">
      <span className="text-[11px] text-gray-400 block mb-0.5">{label}</span>
      <Input
        type="date"
        value={iso}
        max={today}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 text-sm"
        data-testid={testId}
      />
    </label>
  );
};

// Nan-friendly Price-2-Day home hub: three large primary actions + a community
// explainer + two smaller secondary actions. All wording via i18n (6 langs).
const HubAction = ({ icon: Icon, title, sub, onClick, testid, tone }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-start rounded-3xl p-5 flex items-center gap-4 shadow-sm transition-transform active:scale-[0.98] ${tone}`}
  >
    <span className="h-16 w-16 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
      <Icon className="h-8 w-8" />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-2xl font-extrabold leading-tight">{title}</span>
      <span className="block text-base opacity-95 mt-0.5">{sub}</span>
    </span>
    <ChevronRight className="h-7 w-7 shrink-0 opacity-80 rtl:rotate-180" />
  </button>
);

const PriceHub = ({ t, go }) => (
  <div className="space-y-5" data-testid="price-hub">
    <div className="text-center pt-1 pb-1">
      <h2 className="text-3xl font-extrabold text-gray-900">{t('price.homeHeadline')}</h2>
      <p className="text-base text-gray-600 mt-1">{t('price.homeTagline')}</p>
      <p className="text-sm text-gray-500 mt-2 leading-relaxed">{t('price.homeIntro')}</p>
    </div>

    <div className="space-y-3">
      <HubAction icon={ShoppingCart} title={t('price.actListTitle')} sub={t('price.actListSub')} onClick={() => go('list')} testid="hub-shopping-list" tone="bg-emerald-600 text-white hover:bg-emerald-700" />
      <HubAction icon={Search} title={t('price.actSearchTitle')} sub={t('price.actSearchSub')} onClick={() => go('search')} testid="hub-find-price" tone="bg-indigo-600 text-white hover:bg-indigo-700" />
      <HubAction icon={Camera} title={t('price.actScanTitle')} sub={t('price.actScanSub')} onClick={() => go('scan')} testid="hub-scan-share" tone="bg-violet-600 text-white hover:bg-violet-700" />
    </div>

    <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-4" data-testid="price-hub-community">
      <p className="text-lg font-bold text-amber-900">{t('price.helpTitle')}</p>
      <p className="text-sm text-amber-800 mt-1 leading-relaxed">{t('price.helpBody')}</p>
      <div className="mt-3 flex items-start gap-2 rounded-xl bg-white/70 border border-amber-200 p-2.5">
        <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-[13px] text-amber-900 leading-relaxed">{t('price.helpPrivacy')}</p>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <button onClick={() => go('favourites')} data-testid="hub-buy-often" className="rounded-2xl bg-white border-2 border-rose-100 p-4 text-center hover:bg-rose-50 transition-colors">
        <Heart className="h-7 w-7 text-rose-500 fill-rose-500 mx-auto mb-1.5" />
        <span className="block text-base font-bold text-gray-900 leading-tight">{t('price.buyOftenTitle')}</span>
        <span className="block text-[13px] text-gray-500 mt-0.5">{t('price.buyOftenSub')}</span>
      </button>
      <button onClick={() => go('deals')} data-testid="hub-deals" className="rounded-2xl bg-white border-2 border-emerald-100 p-4 text-center hover:bg-emerald-50 transition-colors">
        <Tag className="h-7 w-7 text-emerald-600 mx-auto mb-1.5" />
        <span className="block text-base font-bold text-gray-900 leading-tight">{t('price.dealsBtnTitle')}</span>
        <span className="block text-[13px] text-gray-500 mt-0.5">{t('price.dealsBtnSub')}</span>
      </button>
    </div>
  </div>
);

export default function PricePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepListId = searchParams.get('listId') || null;   // from a "list shared" notification
  const deepTab = searchParams.get('tab');
  const { t } = useTranslation();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(null); // editable extracted data
  const [view, setView] = useState(() => {
    if (deepListId || deepTab === 'list') return 'list';
    if (deepTab === 'search') return 'search';
    if (deepTab === 'scan') return 'scan';
    return 'home';
  }); // 'home' | 'scan' | 'search' | 'list' | 'favourites' | 'deals' | 'products'
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('recent'); // 'recent' | 'price' | 'nearest'
  const [priceTown, setPriceTown] = useState(() => localStorage.getItem('f2d_price_town') || '');
  const [towns, setTowns] = useState([]);
  const [distanceEnabled, setDistanceEnabled] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [detail, setDetail] = useState(null);        // Price Details modal row
  const [editing, setEditing] = useState(false);     // edit mode inside modal
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [promos, setPromos] = useState([]);
  const [favKeys, setFavKeys] = useState(new Set());
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const loadFavKeys = React.useCallback(async () => {
    if (!user) { setFavKeys(new Set()); return; }
    try { const r = await axiosInstance.get('/price/favourites/keys'); setFavKeys(new Set(r.data?.keys || [])); }
    catch { /* ignore */ }
  }, [user]);
  React.useEffect(() => { loadFavKeys(); }, [loadFavKeys]);

  React.useEffect(() => { axiosInstance.get('/price/towns').then((r) => setTowns(r.data?.towns || [])).catch(() => {}); }, []);

  const favDupKey = (name, brand, pack) => `${(name || '').trim().toLowerCase()}|${(brand || '').trim().toLowerCase()}|${(pack || '').trim().toLowerCase()}`;
  const toggleFavourite = async (row) => {
    if (!user) { toast.error(t('price.errSignIn')); navigate('/signin'); return; }
    const name = (row.name || row.normalizedName || row.description || '').trim();
    if (!name) return;
    const key = favDupKey(name, row.brand, row.packSize);
    // optimistic
    setFavKeys((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
    try {
      const r = await axiosInstance.post('/price/favourites/toggle', { name, normalizedName: row.normalizedName || null, brand: row.brand || null, packSize: row.packSize || null });
      toast.success(r.data?.favourited ? t('engage.favAdded', 'Added to Favourites') : t('engage.favRemoved', 'Removed from Favourites'));
    } catch { loadFavKeys(); toast.error(t('price.errGeneric')); }
  };

  // Stable anonymous session id for promotion stats (no personal data).
  const promoSession = () => {
    let s = localStorage.getItem('f2d_session');
    if (!s) { s = 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('f2d_session', s); }
    return s;
  };
  const promoTown = user?.town || user?.city || '';

  const runSearch = async (term, sortBy, townArg) => {
    const query = (term ?? q).trim();
    if (query.length < 2) { toast.error(t('price.errMin2')); return; }
    setSearching(true);
    try {
      const ft = townArg !== undefined ? townArg : priceTown;
      const _tok = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.get(`${API}/price/search`, {
        params: { q: query, sort: sortBy ?? sort, fromTown: ft || '' },
        headers: _tok ? { Authorization: `Bearer ${_tok}` } : {},
      });
      setResults(res.data?.results || []);
      setDistanceEnabled(!!res.data?.distanceEnabled);
      setSearched(true);
      // Sponsored promotions (separate, clearly-marked; NEVER part of ranking).
      try {
        const pr = await axiosInstance.get('/price/promotions/search', { params: { q: query, town: promoTown } });
        const list = pr.data?.promotions || [];
        setPromos(list);
        const sid = promoSession();
        list.forEach((p) => { axiosInstance.post(`/price/promotions/${p.id}/view`, { sessionId: sid }).catch(() => {}); });
      } catch { setPromos([]); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('price.errSearch'));
    } finally {
      setSearching(false);
    }
  };
  const onPromoTap = (p) => {
    axiosInstance.post(`/price/promotions/${p.id}/click`, { sessionId: promoSession() }).catch(() => {});
    if (p.businessId) window.open(`/business/${p.businessId}`, '_blank');
  };
  const changeSort = (s) => setSort(s);
  const changePriceTown = (v) => {
    setPriceTown(v);
    if (v) localStorage.setItem('f2d_price_town', v); else localStorage.removeItem('f2d_price_town');
    if (searched) runSearch(undefined, undefined, v);
  };
  const dirUrl = (r) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${r.shopName || ''}${r.town ? `, ${r.town}` : (r.shopBranch ? `, ${r.shopBranch}` : '')}`)}`;
  const money = (v, cur) => (v === null || v === undefined) ? '' : `${cur ? cur + ' ' : ''}${v}`;
  const FRESH_STYLE = {
    Recent: 'bg-emerald-100 text-emerald-700',
    Older: 'bg-amber-100 text-amber-700',
    Historical: 'bg-gray-200 text-gray-600',
    Unknown: 'bg-gray-100 text-gray-500',
  };
  const FRESH_KEY = { Recent: 'freshRecent', Older: 'freshOlder', Historical: 'freshHistorical' };
  const freshLabel = (label) => t(`price.${FRESH_KEY[label] || 'freshUnknown'}`);

  // Group flat search results into one card per product. Grouping key is the
  // AI-canonical normalizedName; older receipts without it fall back to the
  // cleaned original description. NO fuzzy matching. Uses only fields already
  // returned by the existing /search API — no API change, no data change.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of results) {
      const norm = (r.normalizedName || '').trim();
      const key = norm ? `n:${norm.toLowerCase()}` : `d:${(r.description || '').trim().toLowerCase()}`;
      if (!map.has(key)) map.set(key, { key, name: norm || r.description || '', rows: [] });
      map.get(key).rows.push(r);
    }
    const arr = [...map.values()].map((g) => {
      const prices = g.rows.map((x) => x.price).filter((v) => v != null);
      const lowest = prices.length ? Math.min(...prices) : null;
      const units = g.rows.map((x) => x.unitPrice?.value).filter((v) => v != null);
      const lowestUnit = units.length ? Math.min(...units) : null;
      const unitLabel = g.rows.find((x) => x.unitPrice)?.unitPrice?.label || null;
      const dates = g.rows.map((x) => x.receiptDate).filter(Boolean);
      const newest = dates.length ? dates.slice().sort().slice(-1)[0] : null;
      const currency = g.rows.find((x) => x.currency)?.currency || '';
      const kms = g.rows.map((x) => x.distanceKm).filter((v) => v != null);
      const nearestKm = kms.length ? Math.min(...kms) : null;
      const rows = g.rows.slice().sort((a, b) => {
        if (sort === 'price') return (a.price ?? Infinity) - (b.price ?? Infinity);
        if (sort === 'nearest') return (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity);
        return String(b.receiptDate || '').localeCompare(String(a.receiptDate || ''));
      });
      return { ...g, lowest, lowestUnit, unitLabel, newest, currency, nearestKm, rows };
    });
    arr.sort((a, b) => {
      if (sort === 'price') return (a.lowest ?? Infinity) - (b.lowest ?? Infinity);
      if (sort === 'nearest') return (a.nearestKm ?? Infinity) - (b.nearestKm ?? Infinity);
      return String(b.newest || '').localeCompare(String(a.newest || ''));
    });
    return arr;
  }, [results, sort]);

  const resetAll = () => {
    setForm(null);
    setPreview(null);
    setSaved(false);
    setMpChecks({});
    setMpDone(false);
    if (inputRef.current) inputRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setItemField = (idx, key, val) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleFile = async (file) => {
    if (!file) return;
    setForm(null);
    setSaved(false);
    setBusy(true);
    try {
      let working = file;
      if (isHeic(working)) {
        try { working = await heicToJpeg(working); }
        catch { toast.error(t('price.errHeic')); return; }
      }
      if (!working.type?.startsWith('image/')) { toast.error(t('price.errChooseImage')); return; }
      let compressed;
      try { compressed = await imageCompression(working, COMPRESS_OPTS); }
      catch { compressed = working; }
      setPreview(URL.createObjectURL(compressed));

      const fd = new FormData();
      const name = (compressed.name || 'receipt.jpg').replace(/\.(png|webp|heic|heif)$/i, '.jpg');
      fd.append('file', compressed, (name.endsWith('.jpg') || name.endsWith('.jpeg')) ? name : `${name}.jpg`);
      const res = await axiosInstance.post('/price/scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ex = res.data?.extracted;
      if (!ex) { toast.error(t('price.errNoData')); return; }
      // Seed the editable review form from the extracted data.
      setForm({
        shopName: ex.shopName || '',
        shopLocation: ex.shopLocation || '',
        purchaseDate: ex.purchaseDate || '',
        currency: ex.currency || '',
        items: (Array.isArray(ex.items) ? ex.items : []).map((it) => ({
          description: it.description || '',
          normalizedName: it.normalizedName || '',
          brand: it.brand || '',
          packSize: it.packSize || '',
          quantity: it.quantity ?? '',
          unitPrice: it.unitPrice ?? '',
          lineTotal: it.lineTotal ?? '',
          discount: it.discount || '',
        })),
      });
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error(t('price.errSignIn'));
        navigate('/signin');
      } else {
        toast.error(e?.response?.data?.detail || t('price.errScan'));
      }
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!user) {
      toast.error(t('price.errSignIn'));
      navigate('/signin');
      return;
    }
    const items = (form?.items || []).filter((it) => (it.description || '').trim() !== '');
    if (items.length === 0) { toast.error(t('price.errNoItems')); return; }
    setSaving(true);
    try {
      const payload = {
        shopName: form.shopName || null,
        shopBranch: form.shopLocation || null,
        receiptDate: form.purchaseDate || null,
        currency: form.currency || null,
        items: items.map((it) => ({
          originalDescription: (it.description || '').trim(),
          normalizedName: it.normalizedName || null,
          brand: it.brand || null,
          packSize: it.packSize || null,
          quantity: num(it.quantity),
          unitPrice: num(it.unitPrice),
          lineTotal: num(it.lineTotal),
          discount: it.discount || null,
          notes: it.notes || null,
        })),
      };
      await axiosInstance.post('/price/receipts', payload);
      setSaved(true);
    } catch (e) {
      if (e?.response?.status === 409) toast.error(t('price.errDuplicate'));
      else if (e?.response?.status === 401) { toast.error(t('price.errSignIn')); navigate('/signin'); }
      else toast.error(e?.response?.data?.detail || t('price.errSaveGeneric'));
    } finally {
      setSaving(false);
    }
  };

  // ---- Price Details modal: edit / delete a single submitted price ----
  const openDetail = (r) => {
    setDetail(r); setEditing(false); setConfirmDel(false);
    setHistory(null); setHistoryLoading(true);
    axiosInstance.get('/price/history', { params: { receiptId: r.receiptId, itemId: r.itemId || undefined, index: r.itemIndex } })
      .then((res) => setHistory(res.data?.observations || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };
  const closeDetail = () => { setDetail(null); setEditing(false); setConfirmDel(false); setEditForm(null); setHistory(null); };
  const startEdit = () => {
    setEditForm({
      productName: detail.normalizedName || detail.description || '',
      price: detail.price ?? '',
      brand: detail.brand || '',
      packSize: detail.packSize || '',
      quantity: detail.quantity ?? '',
      shopName: detail.shopName || '',
      town: detail.town || '',
      receiptDate: (detail.receiptDate || '').slice(0, 10),
      notes: detail.notes || '',
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!editForm.productName.trim()) { toast.error(t('priceEdit.errName', 'Please enter a product name')); return; }
    if (editForm.price === '' || Number(editForm.price) <= 0) { toast.error(t('priceEdit.errPrice', 'Please enter a valid price')); return; }
    setSavingEdit(true);
    try {
      await axiosInstance.put(`/price/receipts/${detail.receiptId}/item`, {
        itemId: detail.itemId || null,
        index: detail.itemIndex,
        productName: editForm.productName.trim(),
        price: Number(editForm.price),
        brand: editForm.brand.trim() || null,
        packSize: editForm.packSize.trim() || null,
        quantity: editForm.quantity === '' ? null : Number(editForm.quantity),
        shopName: editForm.shopName.trim() || null,
        town: editForm.town.trim() || null,
        receiptDate: editForm.receiptDate || null,
        notes: editForm.notes.trim() || null,
      });
      toast.success(t('priceEdit.saved', 'Price updated'));
      closeDetail();
      runSearch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('priceEdit.errSave', 'Could not update price'));
    } finally { setSavingEdit(false); }
  };
  const doDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/price/receipts/${detail.receiptId}/item`, {
        params: { itemId: detail.itemId || undefined, index: detail.itemIndex },
      });
      toast.success(t('priceEdit.deleted', 'Price deleted'));
      closeDetail();
      runSearch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('priceEdit.errDelete', 'Could not delete price'));
    } finally { setDeleting(false); }
  };

  const items = form?.items || [];

  // Buy Again — add a product (from a search row or a saved receipt item) to
  // Buy Again — adds the product to the current Shopping List only (per Stage-1
  // spec). If it's already on the list, the server merges the quantity instead
  // of creating a duplicate row. Requires sign-in.
  const buyAgain = async (row) => {
    if (!user) { toast.error(t('price.errSignIn')); navigate('/signin'); return; }
    const nm = (row.normalizedName || row.description || row.name || '').trim();
    if (!nm) return;
    try {
      const res = await axiosInstance.post('/price/shopping-list', { name: nm, qty: 1, brand: row.brand || null, packSize: row.packSize || null });
      toast.success(res.data?.outcome === 'merged' ? t('price.slIncreased') : t('price.buyAgainDone'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); }
  };

  // Post-save: add chosen just-saved items to My Products (user ticks; nothing auto).
  const [mpChecks, setMpChecks] = useState({});
  const [mpBusy, setMpBusy] = useState(false);
  const [mpDone, setMpDone] = useState(false);
  const addSavedToMyProducts = async () => {
    const sel = (form?.items || []).filter((it, i) => mpChecks[i] && (it.description || '').trim());
    if (sel.length === 0) { toast.error(t('price.mpPickOne')); return; }
    setMpBusy(true);
    try {
      const res = await axiosInstance.post('/price/my-products/from-receipt', {
        items: sel.map((it) => ({ name: it.normalizedName || it.description, normalizedName: it.normalizedName || null, brand: it.brand || null, packSize: it.packSize || null })),
      });
      toast.success(t('price.mpFromReceiptDone', { added: res.data?.added || 0, skipped: res.data?.skippedDuplicates || 0 }));
      setMpDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); } finally { setMpBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="price-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate('/')} data-testid="price-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
              <Receipt className="h-4 w-4" />
            </span>
            <div>
              <h1 className="text-base font-bold text-gray-900 leading-tight">Price-2-Day</h1>
              <p className="text-[11px] text-gray-500 leading-tight">{t('price.tagline')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Big obvious Back button on every sub-screen (returns to the simple hub). */}
        {view !== 'home' && (
          <button onClick={() => setView('home')} className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-gray-200 px-5 py-2.5 text-base font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm" data-testid="price-hub-back">
            <ArrowLeft className="h-5 w-5 rtl:rotate-180" />{t('price.backToMenu')}
          </button>
        )}
        {view === 'home' ? (
          <PriceHub t={t} go={setView} />
        ) : view === 'products' ? (
          <MyProductsPanel />
        ) : view === 'list' ? (
          <ShoppingListPanel initialListId={deepListId} autoToday={!deepListId} onManageProducts={() => setView('products')} />
        ) : view === 'favourites' ? (
          <FavouritesPanel />
        ) : view === 'deals' ? (
          <DealsPanel town={promoTown} />
        ) : view === 'search' ? (
          <>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  placeholder={t('price.searchPlaceholder')}
                  className="w-full h-10 pl-9 pr-3 rounded-full border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="price-search-input"
                />
              </div>
              <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={() => runSearch()} disabled={searching} data-testid="price-search-button">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : t('price.searchBtn')}
              </Button>
            </div>

            {searched && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[12px] flex-wrap">
                  <span className="text-gray-400">{t('price.sortLabel')}</span>
                  <button onClick={() => changeSort('price')} className={`px-2.5 py-1 rounded-full ${sort === 'price' ? 'bg-indigo-600 text-white' : 'bg-white border text-gray-600'}`} data-testid="price-sort-price"><TrendingDown className="h-3 w-3 inline mr-1" />{t('price.sortLowest')}</button>
                  <button onClick={() => changeSort('recent')} className={`px-2.5 py-1 rounded-full ${sort === 'recent' ? 'bg-indigo-600 text-white' : 'bg-white border text-gray-600'}`} data-testid="price-sort-recent"><Clock className="h-3 w-3 inline mr-1" />{t('price.sortRecent')}</button>
                  <button onClick={() => changeSort('nearest')} className={`px-2.5 py-1 rounded-full ${sort === 'nearest' ? 'bg-indigo-600 text-white' : 'bg-white border text-gray-600'}`} data-testid="price-sort-nearest"><MapPin className="h-3 w-3 inline mr-1" />{t('price.sortNearest', 'Nearest')}</button>
                </div>
                <select value={priceTown} onChange={(e) => changePriceTown(e.target.value)} className="w-full h-9 px-2 rounded-xl border text-[12px] bg-white" data-testid="price-search-town">
                  <option value="">{t('price.dist.pick', 'Select your town to see distances')}</option>
                  {towns.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
                </select>
              </div>
            )}

            {searched && results.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6" data-testid="price-search-empty">{t('price.noResults')}</p>
            )}

            {/* SPONSORED promotions — clearly separate block, NEVER part of the
                genuine cheapest-price ranking below. */}
            {searched && promos.length > 0 && (
              <div className="space-y-2" data-testid="sponsored-promotions">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="sponsored-label">{t('pricePromo.sponsored', 'Sponsored')}</span>
                  <span className="text-[11px] text-gray-400">{t('pricePromo.sponsoredHint', 'Paid offer — not part of the price comparison')}</span>
                </div>
                {promos.map((p, pi) => (
                  <button
                    key={p.id}
                    onClick={() => onPromoTap(p)}
                    className="w-full text-start rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-3 flex gap-3 hover:bg-amber-50 transition-colors"
                    data-testid={`sponsored-card-${pi}`}
                  >
                    {p.photoUrl && <img src={p.photoUrl} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" data-testid={`sponsored-photo-${pi}`} />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate" data-testid={`sponsored-product-${pi}`}>{p.productName}</p>
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 shrink-0">{t('pricePromo.sponsored', 'Sponsored')}</span>
                      </div>
                      <p className="text-[12px] text-gray-600 truncate">{p.shopName}{p.shopBranch ? ` — ${p.shopBranch}` : ''}{p.town ? ` · ${p.town}` : ''}</p>
                      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                        <span className="text-base font-bold text-amber-700" data-testid={`sponsored-promoprice-${pi}`}>{money(p.promoPrice, p.currency)}</span>
                        {p.normalPrice != null && <span className="text-[12px] text-gray-400 line-through" data-testid={`sponsored-normalprice-${pi}`}>{money(p.normalPrice, p.currency)}</span>}
                        {p.packSize && <span className="text-[11px] text-gray-500">· {p.packSize}</span>}
                      </div>
                      {p.description && <p className="text-[11px] text-gray-500 truncate">{p.description}</p>}
                      <p className="text-[10px] text-gray-400">{p.availabilityText ? `${p.availabilityText} · ` : ''}{t('pricePromo.ends', 'ends')} {(p.endDate || '').slice(0, 10)}</p>
                    </div>
                  </button>
                ))}
                <div className="border-t border-gray-100 pt-1" />
              </div>
            )}

            {results.length > 0 && (
              <div className="space-y-3" data-testid="price-search-results">
                {groups.map((g, gi) => (
                  <div key={g.key} className="rounded-xl bg-white border p-3" data-testid={`price-group-${gi}`}>
                    {/* Product header */}
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900 min-w-0 truncate" data-testid={`price-group-name-${gi}`}>{g.name}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        {(() => { const fk = favDupKey(g.name, g.rows[0]?.brand, g.rows[0]?.packSize); const on = favKeys.has(fk); return (
                          <button onClick={() => toggleFavourite({ name: g.name, normalizedName: g.rows[0]?.normalizedName, brand: g.rows[0]?.brand, packSize: g.rows[0]?.packSize })} aria-label={t('engage.favourite', 'Favourite')} data-testid={`price-fav-${gi}`}>
                            <Heart className={`h-4 w-4 transition-colors ${on ? 'text-rose-500 fill-rose-500' : 'text-gray-300 hover:text-rose-400'}`} />
                          </button>
                        ); })()}
                        <span className="text-[11px] text-gray-400" data-testid={`price-group-count-${gi}`}>{t('price.shopsCount', { count: g.rows.length })}</span>
                      </div>
                    </div>
                    {/* Lowest price / lowest unit price */}
                    <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                      <span className="text-[11px] text-gray-500">{t('price.lowestPrice')}:</span>
                      <span className="text-base font-bold text-indigo-700" data-testid={`price-group-lowest-${gi}`}>{money(g.lowest, g.currency) || '—'}</span>
                      {g.lowestUnit != null && (
                        <span className="text-[12px] text-emerald-700" data-testid={`price-group-lowest-unit-${gi}`}>· {t('price.lowestUnit')}: {g.currency ? g.currency + ' ' : ''}{g.lowestUnit} {g.unitLabel}</span>
                      )}
                      <button onClick={() => buyAgain(g.rows[0])} className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-indigo-600 text-white inline-flex items-center gap-1" data-testid={`price-buy-again-${gi}`}>
                        <RotateCcw className="h-3 w-3" />{t('price.buyAgain')}
                      </button>
                    </div>
                    {/* Per-shop rows */}
                    <div className="mt-2 border-t border-gray-100 divide-y divide-gray-100">
                      {g.rows.map((r, ri) => {
                        const isLowest = r.price != null && g.lowest != null && r.price === g.lowest;
                        return (
                          <div key={ri} onClick={() => openDetail(r)} role="button" tabIndex={0} className={`py-2 flex items-start justify-between gap-2 cursor-pointer hover:bg-slate-50 transition-colors rounded px-1 ${isLowest ? 'bg-indigo-50/50' : ''}`} data-testid={`price-row-open-${gi}-${ri}`}>
                            <div className="min-w-0">
                              <p className="text-[13px] text-gray-800 truncate">
                                <span className="font-medium">{r.shopName || t('price.unknownShop')}</span>{r.shopBranch ? ` — ${r.shopBranch}` : ''}
                              </p>
                              <p className="text-[11px] text-gray-400">
                                {t('price.receiptDated')} {r.receiptDate || '—'}{r.discount ? ` · ${t('price.offer')}: ${r.discount}` : ''}
                              </p>
                              {r.locationStatus === 'ok' ? (
                                <p className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" data-testid={`price-group-${gi}-row-${ri}-dist`}>
                                  <span className="inline-flex items-center gap-1 text-gray-500"><MapPin className="h-3 w-3" />{r.distanceKm != null ? `~${r.distanceKm} km` : r.town}</span>
                                  <a href={dirUrl(r)} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-indigo-600 font-medium" data-testid={`price-group-${gi}-row-${ri}-directions`}><Navigation className="h-3 w-3" />{t('price.dist.directions', 'Directions')}</a>
                                </p>
                              ) : (distanceEnabled && (
                                <p className="text-[11px] text-gray-400 mt-0.5 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{t('price.dist.unavailable', 'Location not yet available')}</p>
                              ))}
                              {r.normalizedName && r.description && r.description !== g.name ? (
                                <p className="text-[10px] text-gray-300 truncate">{t('price.asPrinted')}: {r.description}</p>
                              ) : null}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="flex items-center gap-1 justify-end">
                                <span className={`text-sm font-bold ${isLowest ? 'text-indigo-700' : 'text-gray-800'}`} data-testid={`price-group-${gi}-row-${ri}-price`}>{money(r.price, r.currency) || '—'}</span>
                                {isLowest && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-600 text-white" data-testid={`price-group-${gi}-row-${ri}-lowest`}>{t('price.lowestPrice')}</span>}
                              </div>
                              {r.unitPrice ? (
                                <p className="text-[11px] text-gray-500" data-testid={`price-group-${gi}-row-${ri}-unit`}>{r.currency ? r.currency + ' ' : ''}{r.unitPrice.value} {r.unitPrice.label}</p>
                              ) : (r.packSize ? <p className="text-[11px] text-gray-400">{r.packSize}</p> : null)}
                              <span className={`inline-block mt-0.5 text-[10px] px-2 py-0.5 rounded-full ${FRESH_STYLE[r.freshness?.label] || FRESH_STYLE.Unknown}`} data-testid={`price-group-${gi}-row-${ri}-freshness`}>{freshLabel(r.freshness?.label)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searched && (
              <p className="text-[11px] text-gray-400 leading-snug pt-1" data-testid="price-disclaimer">{t('price.disclaimer')}</p>
            )}
          </>
        ) : saved ? (
          <div className="rounded-2xl bg-white border shadow-sm p-6 text-center" data-testid="price-saved">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-extrabold text-gray-900" data-testid="price-saved-message">{t('price.savedCommunityTitle')}</h2>
            <p className="text-base text-gray-600 mt-1 mb-4 leading-relaxed">{t('price.savedCommunityBody')}</p>

            {/* Optional: add just-saved items to My Products (user chooses; nothing auto). */}
            {!mpDone && (form?.items || []).length > 0 && (
              <div className="text-left rounded-xl border bg-slate-50 p-3 mb-4" data-testid="price-saved-addmp">
                <p className="text-[13px] font-semibold text-gray-800 flex items-center gap-1.5 mb-2"><Package className="h-4 w-4 text-indigo-600" />{t('price.addToMyProductsTitle')}</p>
                <div className="max-h-48 overflow-auto space-y-1">
                  {(form.items || []).filter((it) => (it.description || '').trim()).map((it, i) => (
                    <label key={i} className="flex items-center gap-2 py-1 text-sm" data-testid={`price-saved-mp-item-${i}`}>
                      <input type="checkbox" checked={!!mpChecks[i]} onChange={(e) => setMpChecks((c) => ({ ...c, [i]: e.target.checked }))} className="h-4 w-4" data-testid={`price-saved-mp-check-${i}`} />
                      <span className="truncate">{it.normalizedName || it.description}</span>
                    </label>
                  ))}
                </div>
                <Button className="w-full mt-2 rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={addSavedToMyProducts} disabled={mpBusy} data-testid="price-saved-mp-add">
                  {mpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ListPlus className="h-4 w-4 mr-1" />{t('price.addToMyProductsBtn')}</>}
                </Button>
              </div>
            )}
            {mpDone && <p className="text-[12px] text-emerald-700 mb-3" data-testid="price-saved-mp-done">{t('price.addedToMyProducts')}</p>}

            <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={resetAll} data-testid="price-scan-another">
              <Camera className="h-4 w-4 mr-2" />{t('price.scanAnother')}
            </Button>
          </div>
        ) : !user ? (
          <div className="rounded-2xl bg-white border shadow-sm p-8 text-center" data-testid="price-scan-signin">
            <div className="mx-auto h-14 w-14 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
              <ScanLine className="h-7 w-7 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{t('price.scanTitle')}</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4">{t('price.signInToScan', 'Please sign in to scan and submit a receipt.')}</p>
            <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate('/signin')} data-testid="price-scan-signin-btn">
              {t('price.signIn', 'Sign in')}
            </Button>
          </div>
        ) : (
          <>
            {/* Why scan? — community benefit explainer (shown before uploading) */}
            {!form && !saved && (
              <div className="rounded-2xl bg-violet-50 border-2 border-violet-200 p-4" data-testid="price-scan-why">
                <p className="text-lg font-bold text-violet-900 flex items-center gap-2"><Camera className="h-5 w-5" />{t('price.scanWhyTitle')}</p>
                <p className="text-sm text-violet-800 mt-1 leading-relaxed">{t('price.scanWhyBody')}</p>
              </div>
            )}
            {/* Receipt privacy notice — shown before uploading */}
            {!form && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed" data-testid="price-receipt-privacy">
                {t('price.receiptPrivacy')}
              </div>
            )}
            {/* Prototype notice */}
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800" data-testid="price-prototype-note">
              {t('price.reviewNote')}
            </div>

            {/* Scan control (hidden once a form is loaded, shown again after clearing) */}
            {!form && (
              <div className="rounded-2xl bg-white border shadow-sm p-5 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                  {busy ? <Loader2 className="h-7 w-7 text-indigo-600 animate-spin" /> : <ScanLine className="h-7 w-7 text-indigo-600" />}
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{t('price.scanTitle')}</h2>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  {t('price.scanBlurb')}
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                  {/* Take Photo — opens the camera */}
                  <button
                    type="button"
                    onClick={() => { if (!busy) cameraRef.current?.click(); }}
                    disabled={busy}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                      busy ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                    data-testid="price-take-photo-button"
                  >
                    <Camera className="h-4 w-4" />
                    {busy ? t('price.readingReceipt') : t('price.takePhoto')}
                  </button>
                  {/* Choose Existing Photo — opens the gallery / file chooser */}
                  <button
                    type="button"
                    onClick={() => { if (!busy) inputRef.current?.click(); }}
                    disabled={busy}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors border ${
                      busy ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50'
                    }`}
                    data-testid="price-choose-photo-button"
                  >
                    <ImageIcon className="h-4 w-4" />
                    {t('price.choosePhoto')}
                  </button>
                  {/* Camera input (capture forces rear camera on mobile) */}
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    capture="environment"
                    disabled={busy}
                    className="hidden"
                    onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ''; handleFile(f); }}
                    data-testid="price-camera-input"
                  />
                  {/* Gallery / file input (no capture → OS photo/file chooser) */}
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    disabled={busy}
                    className="hidden"
                    onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ''; handleFile(f); }}
                    data-testid="price-scan-input"
                  />
                </div>
                {preview && (
                  <div className="mt-4">
                    <img src={preview} alt={t('price.receiptPreviewAlt')} className="mx-auto max-h-56 rounded-lg border object-contain" data-testid="price-receipt-preview" />
                  </div>
                )}
              </div>
            )}

            {/* Editable review */}
            {form && (
              <div className="rounded-2xl bg-white border shadow-sm overflow-hidden" data-testid="price-review">
                <div className="px-5 py-3 border-b bg-slate-50">
                  <h3 className="text-sm font-bold text-gray-800">{t('price.reviewTitle')}</h3>
                  <p className="text-[11px] text-gray-500">{t('price.reviewSubtitle')}</p>
                </div>

                {/* Receipt-level fields */}
                <div className="px-5 py-4 grid grid-cols-2 gap-3">
                  <Field label={t('price.fShop')} value={form.shopName} onChange={(v) => setField('shopName', v)} testId="price-field-shop" />
                  <Field label={t('price.fLocation')} value={form.shopLocation} onChange={(v) => setField('shopLocation', v)} testId="price-field-location" />
                  <DateField label={t('price.fDate')} value={form.purchaseDate} onChange={(v) => setField('purchaseDate', v)} testId="price-field-date" />
                  <Field label={t('price.fCurrency')} value={form.currency} onChange={(v) => setField('currency', v)} placeholder="EUR" testId="price-field-currency" />
                </div>

                {/* Items */}
                <div className="px-3 pb-3">
                  <div className="text-[11px] font-semibold text-gray-400 px-2 pb-2">{t('price.itemsLabel', { count: items.length })}</div>
                  {items.length === 0 ? (
                    <p className="text-sm text-gray-500 px-2 pb-2">{t('price.noItems')}</p>
                  ) : (
                    <div className="space-y-3">
                      {items.map((it, idx) => (
                        <div key={idx} className="rounded-lg border border-gray-100 bg-slate-50/60 p-3" data-testid={`price-review-item-${idx}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className="text-[11px] font-semibold text-gray-400">{t('price.itemN', { n: idx + 1 })}</span>
                            <button
                              type="button"
                              onClick={() => removeItem(idx)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                              aria-label={t('price.removeItem')}
                              data-testid={`price-item-remove-${idx}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="mb-2">
                            <Field label={t('price.fDesc')} value={it.description} onChange={(v) => setItemField(idx, 'description', v)} testId={`price-item-desc-${idx}`} />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Field label={t('price.fBrand')} value={it.brand} onChange={(v) => setItemField(idx, 'brand', v)} testId={`price-item-brand-${idx}`} />
                            <Field label={t('price.fPackSize')} value={it.packSize} onChange={(v) => setItemField(idx, 'packSize', v)} testId={`price-item-packsize-${idx}`} />
                            <Field label={t('price.fQty')} value={it.quantity} onChange={(v) => setItemField(idx, 'quantity', v)} testId={`price-item-qty-${idx}`} />
                            <Field label={t('price.fUnitPrice')} value={it.unitPrice} onChange={(v) => setItemField(idx, 'unitPrice', v)} testId={`price-item-unitprice-${idx}`} />
                            <Field label={t('price.fLineTotal')} value={it.lineTotal} onChange={(v) => setItemField(idx, 'lineTotal', v)} testId={`price-item-linetotal-${idx}`} />
                            <Field label={t('price.fDiscount')} value={it.discount} onChange={(v) => setItemField(idx, 'discount', v)} testId={`price-item-discount-${idx}`} />
                          </div>
                          <div className="mt-2">
                            <label className="text-[11px] font-medium text-gray-500 flex items-center gap-1"><StickyNote className="h-3 w-3" />{t('priceEdit.notes', 'Notes')} <span className="text-gray-300">({t('priceEdit.optional', 'optional')})</span></label>
                            <textarea value={it.notes || ''} maxLength={500} onChange={(e) => setItemField(idx, 'notes', e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder={t('priceEdit.notesPh', 'e.g. on special offer, loyalty price, bulk pack…')} data-testid={`price-item-notes-${idx}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-5 py-4 border-t bg-white flex items-center gap-2">
                  <Button
                    className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
                    onClick={save}
                    disabled={saving}
                    data-testid="price-save-button"
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {saving ? t('price.saving') : t('price.confirmSave')}
                  </Button>
                  <Button variant="outline" className="rounded-full" onClick={resetAll} disabled={saving} data-testid="price-cancel-button">
                    {t('price.cancel')}
                  </Button>
                </div>
                {!user && (
                  <p className="px-5 pb-4 -mt-2 text-[11px] text-amber-700" data-testid="price-signin-hint">
                    {t('price.signInHint')}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ---------- PRICE DETAILS MODAL (view + owner/admin edit/delete) ---------- */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={closeDetail} data-testid="price-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
              <h3 className="text-base font-bold text-gray-900">{t('priceEdit.title', 'Price details')}</h3>
              <button onClick={closeDetail} className="text-gray-400 hover:text-gray-700" aria-label={t('price.close', 'Close')} data-testid="price-detail-close"><X className="h-5 w-5" /></button>
            </div>

            {!editing ? (
              <div className="px-5 py-4 space-y-3" data-testid="price-detail-view">
                <div>
                  <p className="text-lg font-bold text-gray-900">{detail.normalizedName || detail.description}</p>
                  <p className="text-2xl font-extrabold text-indigo-700 mt-1" data-testid="price-detail-price">{money(detail.price, detail.currency) || '—'}</p>
                </div>
                <dl className="text-sm text-gray-700 space-y-1.5">
                  {detail.brand && <div className="flex justify-between gap-3"><dt className="text-gray-400">{t('priceEdit.brand', 'Brand')}</dt><dd className="font-medium text-right">{detail.brand}</dd></div>}
                  {detail.packSize && <div className="flex justify-between gap-3"><dt className="text-gray-400">{t('priceEdit.packSize', 'Pack / size')}</dt><dd className="font-medium text-right">{detail.packSize}</dd></div>}
                  {detail.quantity != null && <div className="flex justify-between gap-3"><dt className="text-gray-400">{t('priceEdit.quantity', 'Quantity')}</dt><dd className="font-medium text-right">{detail.quantity}</dd></div>}
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">{t('priceEdit.shop', 'Supplier / shop')}</dt><dd className="font-medium text-right">{detail.shopName || t('price.unknownShop')}{detail.shopBranch ? ` — ${detail.shopBranch}` : ''}</dd></div>
                  {detail.town && <div className="flex justify-between gap-3"><dt className="text-gray-400">{t('priceEdit.town', 'Town / location')}</dt><dd className="font-medium text-right">{detail.town}</dd></div>}
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">{t('priceEdit.date', 'Date recorded')}</dt><dd className="font-medium text-right">{detail.receiptDate || '—'}</dd></div>
                </dl>
                {detail.notes && (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3" data-testid="price-detail-notes">
                    <p className="text-[11px] font-semibold text-amber-700 flex items-center gap-1 mb-1"><StickyNote className="h-3.5 w-3.5" />{t('priceEdit.notes', 'Notes')}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{detail.notes}</p>
                  </div>
                )}
                <PriceTrend
                  observations={history}
                  loading={historyLoading}
                  money={money}
                  labels={{
                    title: t('priceHistory.title', 'Price history'),
                    none: t('priceHistory.none', 'No price history yet'),
                    noneHint: t('priceHistory.noneHint', 'More recorded prices for this product at this shop will build a trend here.'),
                    latest: t('priceHistory.latest', 'LATEST'),
                    rose: t('priceHistory.rose', 'Price rose'),
                    fell: t('priceHistory.fell', 'Price fell'),
                    flat: t('priceHistory.flat', 'About the same'),
                  }}
                />
                {detail.canEdit && (
                  <div className="flex gap-2 pt-2">
                    <Button onClick={startEdit} className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="price-edit-button"><Pencil className="h-4 w-4 mr-1" />{t('priceEdit.edit', 'Edit price')}</Button>
                    <Button onClick={() => setConfirmDel(true)} variant="outline" className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="price-delete-button"><Trash2 className="h-4 w-4 mr-1" />{t('priceEdit.delete', 'Delete')}</Button>
                  </div>
                )}
                {confirmDel && (
                  <div className="rounded-xl bg-rose-50 border border-rose-100 p-3 space-y-2" data-testid="price-delete-confirm">
                    <p className="text-sm text-rose-700 font-medium">{t('priceEdit.confirmDelete', 'Delete this price permanently? This cannot be undone.')}</p>
                    <div className="flex gap-2">
                      <Button onClick={doDelete} disabled={deleting} className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700" data-testid="price-delete-confirm-yes">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('priceEdit.confirmYes', 'Yes, delete')}</Button>
                      <Button onClick={() => setConfirmDel(false)} variant="outline" className="rounded-full" data-testid="price-delete-confirm-no">{t('priceEdit.cancel', 'Cancel')}</Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-5 py-4 space-y-3" data-testid="price-edit-form">
                <Field label={t('priceEdit.productName', 'Product name')} value={editForm.productName} onChange={(v) => setEditForm((f) => ({ ...f, productName: v }))} testId="price-edit-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('priceEdit.price', 'Price')} value={editForm.price} onChange={(v) => setEditForm((f) => ({ ...f, price: v }))} testId="price-edit-price" />
                  <Field label={t('priceEdit.quantity', 'Quantity')} value={editForm.quantity} onChange={(v) => setEditForm((f) => ({ ...f, quantity: v }))} testId="price-edit-qty" />
                  <Field label={t('priceEdit.brand', 'Brand')} value={editForm.brand} onChange={(v) => setEditForm((f) => ({ ...f, brand: v }))} testId="price-edit-brand" />
                  <Field label={t('priceEdit.packSize', 'Pack / size')} value={editForm.packSize} onChange={(v) => setEditForm((f) => ({ ...f, packSize: v }))} testId="price-edit-packsize" />
                  <Field label={t('priceEdit.shop', 'Supplier / shop')} value={editForm.shopName} onChange={(v) => setEditForm((f) => ({ ...f, shopName: v }))} testId="price-edit-shop" />
                  <Field label={t('priceEdit.town', 'Town / location')} value={editForm.town} onChange={(v) => setEditForm((f) => ({ ...f, town: v }))} testId="price-edit-town" />
                </div>
                <DateField label={t('priceEdit.date', 'Date recorded')} value={editForm.receiptDate} onChange={(v) => setEditForm((f) => ({ ...f, receiptDate: v }))} testId="price-edit-date" />
                <div>
                  <label className="text-[11px] font-medium text-gray-500 flex items-center gap-1"><StickyNote className="h-3 w-3" />{t('priceEdit.notes', 'Notes')} <span className="text-gray-300">({t('priceEdit.optional', 'optional')})</span></label>
                  <textarea value={editForm.notes} maxLength={500} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} rows={3} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder={t('priceEdit.notesPh', 'e.g. on special offer, loyalty price, bulk pack…')} data-testid="price-edit-notes" />
                  <p className="text-[10px] text-gray-400 text-right">{(editForm.notes || '').length}/500</p>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={saveEdit} disabled={savingEdit} className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="price-edit-save">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : t('priceEdit.save', 'Save changes')}</Button>
                  <Button onClick={() => setEditing(false)} variant="outline" className="rounded-full" data-testid="price-edit-cancel">{t('priceEdit.cancel', 'Cancel')}</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
